import { nanoid } from "nanoid";
import { z } from "zod";

const GUEST_SESSION_KEY = "illumi_guest_session";
const GRACE_PERIOD_MS = 5 * 60 * 1000; // 5 minutes

const guestSessionSchema = z.object({
	id: z.string(),
	lastSeen: z.number(),
});

type GuestSession = z.infer<typeof guestSessionSchema>;

function readSession(): GuestSession | null {
	try {
		const stored = sessionStorage.getItem(GUEST_SESSION_KEY);
		if (!stored) return null;
		const parsed: unknown = JSON.parse(stored);
		const result = guestSessionSchema.safeParse(parsed);
		return result.success ? result.data : null;
	} catch {
		return null;
	}
}

function writeSession(id: string): void {
	try {
		const session: GuestSession = { id, lastSeen: Date.now() };
		sessionStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
	} catch {
		// storage may be unavailable in strict/private browsing settings
	}
}

/**
 * Returns a stable guest session ID that persists across page reloads
 * for up to 5 minutes of inactivity. After the grace period the user
 * gets a fresh ID (and therefore a new persona).
 */
export function getGuestSessionId(opts?: { touch?: boolean }): string {
	const shouldTouch = opts?.touch ?? true;
	const session = readSession();
	if (session && Date.now() - session.lastSeen < GRACE_PERIOD_MS) {
		if (shouldTouch) {
			writeSession(session.id); // refresh lastSeen
		}
		return session.id;
	}

	if (!shouldTouch) {
		return "";
	}

	const id = `guest-${nanoid(10)}`;
	writeSession(id);
	return id;
}
